"use client";

import {
  Button,
  HStack,
  Listbox,
  Spinner,
  Stack,
  Text,
  type Tokens,
  createListCollection,
} from "@chakra-ui/react";
import { memo, useCallback, useMemo } from "react";
import type { GenericOptionItem } from "./option.types";

type ListboxValueChangeDetails = {
  value: string[];
};

export type GenericListboxProps<T> = {
  previewItems: readonly GenericOptionItem[];
  stateObject: T[];
  stateObjectSetter: (value: T[]) => void;
  sourceItems?: readonly T[];
  itemKey?: keyof T;
  getItemByValueCustom?: (value: string) => T | undefined;
  allowMultiple?: boolean;
  disabled?: boolean;
  disableClear?: boolean;
  isLoading?: boolean;
  onClear?: () => void;
  labelText?: string;
  width?: Tokens["sizes"];
  inputId?: string;
  inputName?: string;
  showDescription?: boolean;
  suppressDefaultStyles?: boolean;
  listMaxH?: string;
  showBulkActions?: boolean;
};

function LoadingIndicator() {
  return (
    <HStack flex="1">
      <Spinner size="sm" />
      <Text>Loading...</Text>
    </HStack>
  );
}

function getValueFromStateObject<T>(item: T, itemKey: keyof T) {
  if (item && typeof item === "object" && itemKey in item) {
    return String(item[itemKey]);
  }

  return String(item);
}

function GenericListboxInner<T>({
  previewItems,
  stateObject,
  stateObjectSetter,
  sourceItems,
  itemKey = "id" as keyof T,
  getItemByValueCustom,
  allowMultiple = true,
  disabled = false,
  disableClear = false,
  isLoading = false,
  onClear,
  labelText,
  width,
  inputId,
  inputName,
  showDescription = false,
  suppressDefaultStyles = false,
  listMaxH,
  showBulkActions = allowMultiple,
}: GenericListboxProps<T>) {
  const collection = useMemo(
    () =>
      createListCollection({
        items: [...previewItems],
        itemToString: (item) => item.label,
        itemToValue: (item) => item.value,
      }),
    [previewItems],
  );

  const selectedValues = useMemo(
    () => stateObject.map((item) => getValueFromStateObject(item, itemKey)),
    [itemKey, stateObject],
  );

  const getItemByValueInternal = useMemo(() => {
    if (!sourceItems) return undefined;

    return (value: string) =>
      sourceItems.find((item) => String(item[itemKey]) === value);
  }, [itemKey, sourceItems]);

  const getItemByValue = getItemByValueCustom || getItemByValueInternal;

  const updateSelectedValues = useCallback(
    (values: string[]) => {
      const nextValues = allowMultiple ? values : values.slice(0, 1);

      if (getItemByValue) {
        const foundItems = nextValues
          .map((value) => getItemByValue(value))
          .filter(Boolean) as T[];
        stateObjectSetter(foundItems);
        return;
      }

      stateObjectSetter(nextValues as T[]);
    },
    [allowMultiple, getItemByValue, stateObjectSetter],
  );

  const handleValueChange = useCallback(
    (details: ListboxValueChangeDetails) => {
      updateSelectedValues(details.value);
    },
    [updateSelectedValues],
  );

  const handleSelectAll = useCallback(() => {
    updateSelectedValues(previewItems.map((item) => item.value));
  }, [previewItems, updateSelectedValues]);

  const handleClear = useCallback(() => {
    stateObjectSetter([]);
    onClear?.();
  }, [onClear, stateObjectSetter]);

  return (
    <Stack gap="1" w={width}>
      <Listbox.Root
        collection={collection}
        value={selectedValues}
        onValueChange={handleValueChange}
        selectionMode={allowMultiple ? "multiple" : "single"}
        width="full"
        disabled={disabled}
        variant={suppressDefaultStyles ? "plain" : "subtle"}
      >
        <HStack justify="space-between" align="center" gap="3">
          {labelText && <Listbox.Label>{labelText}</Listbox.Label>}
          {showBulkActions && !isLoading && (
            <HStack gap="1">
              <Button
                type="button"
                size="xs"
                variant="ghost"
                disabled={disabled}
                onClick={handleSelectAll}
              >
                Select all
              </Button>
              {!disableClear && (
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  disabled={disabled}
                  onClick={handleClear}
                >
                  Clear
                </Button>
              )}
            </HStack>
          )}
        </HStack>

        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <Listbox.Content
            id={inputId}
            aria-label={labelText}
            maxH={listMaxH}
            overflowY={listMaxH ? "auto" : undefined}
          >
            {collection.items.map((item) => (
              <Listbox.Item
                item={item}
                key={item.value}
                borderRadius="md"
                px="2"
                py="1.5"
                _selected={{
                  bg: "moveon.secondaryLight",
                  color: "moveon.secondary",
                }}
              >
                <Stack flex="1" gap="0">
                  <Listbox.ItemText>{item.label}</Listbox.ItemText>
                  {showDescription && item.description && (
                    <Text color="moveon.text.muted" fontSize="xs">
                      {item.description}
                    </Text>
                  )}
                </Stack>
                <Listbox.ItemIndicator color="moveon.secondary" />
              </Listbox.Item>
            ))}
          </Listbox.Content>
        )}
      </Listbox.Root>

      {inputName && (
        <input
          type="hidden"
          name={inputName}
          value={selectedValues.join(",")}
          readOnly
        />
      )}
    </Stack>
  );
}

const GenericListbox = memo(GenericListboxInner) as <T>(
  props: GenericListboxProps<T>,
) => JSX.Element;

export default GenericListbox;
